import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import TopCommandBar from '@/components/calendar/TopCommandBar';
import MetricsHeaderStrip from '@/components/calendar/MetricsHeaderStrip';
import CalendarCanvas from '@/components/calendar/CalendarCanvas';
import AppointmentDrawer from '@/components/calendar/AppointmentDrawer';
import DailyOperationsPanel from '@/components/calendar/DailyOperationsPanel';
import CreateAppointmentDialog from '@/components/calendar/CreateAppointmentDialog';
import { CAL } from '@/constants/testIds';

// Compute week range from a date
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7; // Monday=0
  return addDays(x, -dow);
};

export default function CalendarPage() {
  useAuth();
  const [view, setView] = useState('day'); // day | week | resource | agenda
  const [cursor, setCursor] = useState(new Date());
  const [filters, setFilters] = useState({ clinic_id: '', audiologist_id: '' });
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedAptId, setSelectedAptId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Reference data
  const [clinics, setClinics] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [patients, setPatients] = useState([]);
  const [aptTypes, setAptTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load ref data once
  useEffect(() => {
    (async () => {
      try {
        const [c, e, p, t, r] = await Promise.all([
          api.get('/clinics'),
          api.get('/employees'),
          api.get('/patients?limit=2000'),
          api.get('/appointment-types'),
          api.get('/rooms'),
        ]);
        setClinics(c.data);
        setEmployees(e.data);
        setPatients(p.data);
        setAptTypes(t.data);
        setRooms(r.data);
      } catch (err) {
        toast.error('Failed to load reference data');
      }
    })();
  }, []);

  // Date range for query
  const range = useMemo(() => {
    if (view === 'week' || view === 'resource') {
      const s = startOfWeek(cursor);
      return { from: s, to: addDays(s, 7) };
    }
    if (view === 'agenda') {
      const s = startOfDay(cursor);
      return { from: s, to: addDays(s, 30) };
    }
    // day
    const s = startOfDay(cursor);
    return { from: s, to: addDays(s, 1) };
  }, [view, cursor]);

  // Fetch appointments for range
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      };
      if (filters.clinic_id) params.clinic_id = filters.clinic_id;
      if (filters.audiologist_id) params.audiologist_id = filters.audiologist_id;
      const { data } = await api.get('/appointments', { params });
      setAppointments(data);
    } catch (err) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [range, filters]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments, refreshKey]);

  // Fetch live metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const params = {};
      if (filters.clinic_id) params.clinic_id = filters.clinic_id;
      const { data } = await api.get('/metrics/live', { params });
      setMetrics(data);
    } catch { /* silent */ }
  }, [filters.clinic_id]);

  useEffect(() => {
    fetchMetrics();
    const t = setInterval(fetchMetrics, 30000);
    return () => clearInterval(t);
  }, [fetchMetrics, refreshKey]);

  const bump = () => setRefreshKey((k) => k + 1);

  // Lookup helpers
  const lookups = useMemo(() => ({
    patient: Object.fromEntries(patients.map(p => [p.id, p])),
    clinic: Object.fromEntries(clinics.map(c => [c.id, c])),
    employee: Object.fromEntries(employees.map(e => [e.id, e])),
    aptType: Object.fromEntries(aptTypes.map(t => [t.id, t])),
    room: Object.fromEntries(rooms.map(r => [r.id, r])),
  }), [patients, clinics, employees, aptTypes, rooms]);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid={CAL.container}>
      <TopCommandBar
        view={view}
        setView={setView}
        cursor={cursor}
        setCursor={setCursor}
        filters={filters}
        setFilters={setFilters}
        clinics={clinics}
        employees={employees}
        onCreate={() => setOpenCreate(true)}
      />
      <MetricsHeaderStrip metrics={metrics} />
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 overflow-hidden flex flex-col min-w-0 min-h-0">
          <CalendarCanvas
            view={view}
            cursor={cursor}
            range={range}
            appointments={appointments}
            employees={employees}
            lookups={lookups}
            loading={loading}
            onSelectAppointment={setSelectedAptId}
            onAppointmentMoved={async (aptId, newStart, newEnd) => {
              try {
                await api.patch(`/appointments/${aptId}?override=true`, {
                  start_ts: newStart.toISOString(),
                  end_ts: newEnd.toISOString(),
                });
                toast.success('Appointment rescheduled');
                bump();
              } catch (err) {
                toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Failed to move');
              }
            }}
          />
        </div>
        {selectedAptId ? (
          <AppointmentDrawer
            appointmentId={selectedAptId}
            lookups={lookups}
            clinics={clinics}
            employees={employees}
            rooms={rooms}
            aptTypes={aptTypes}
            onClose={() => setSelectedAptId(null)}
            onChanged={bump}
          />
        ) : (
          <DailyOperationsPanel
            appointments={appointments}
            lookups={lookups}
            metrics={metrics}
            onSelectAppointment={setSelectedAptId}
          />
        )}
      </div>
      <CreateAppointmentDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        clinics={clinics}
        employees={employees}
        patients={patients}
        rooms={rooms}
        aptTypes={aptTypes}
        defaultDate={cursor}
        onCreated={(newApt) => {
          bump();
          setSelectedAptId(newApt.id);
        }}
        onPatientCreated={(p) => setPatients((prev) => [p, ...prev])}
      />
    </div>
  );
}
